import cn from "classnames"
import React, { useEffect, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Form } from "reactstrap"
import {
    CleanHtml,
    merchantSelectors,
    PageContainer,
    PageWrapper,
    ShowErrorMessages,
    SpinnerContainer,
    StandardButton,
    useSelector,
    valFuncRequired,
} from "swiipe.portal.shared"
import SubmitButton from "../../components/buttons/SubmitButton"
import FixedLabelInput from "../../components/form/input/FixedLabelInput"
import CancelService from "../../components/merchant/CancelService"
import GoBackContainer from "../../components/navigations/GoBackContainer"
import { SharedCancelContainer } from "../../components/servicesonboarding/SharedCancelContainer"
import { getImage } from "../../services/imageLanguageService"
import { getWebshopHostName } from "../../services/merchantService"
import { useRequiredUserRelations } from "../../services/userRelationService"
import { StoreState } from "../../store/StoreState"
import { gatewaySelectors, IGatewayDto } from "../../store/reducers/gatewayReducer"
import {
    getTermsTechOnboardingDetailsThunk,
    ITermsTechGetOnboardingDetailsResponse,
    ITermsTechOnboardingConnectData,
    termsTechDisableOnboardingThunk,
    termsTechOnboardingConnectThunk,
    TTermsTechOnboardingStatus,
} from "../../store/thunks/area42Thunks"
import { getOnboardingStatusThunk } from "../../store/thunks/swiipePaymentsThunks"
import { sendServiceCancellationEmailThunk } from "../../store/thunks/swiipeServicesThunks"
import { useReduxDispatch } from "../../store/useReduxDispatch"
import "./TermsTechOnboardingPage.scss"
import {
    IFormModel,
    ITermsTechOnboardingPageInnerProps,
    ITermsTechOnboardingPageProps,
    TTermsTechOnboardingFlow,
} from "./TermsTechProps"

const TermsTechOnboardingPage = (props: ITermsTechOnboardingPageProps) => {
    useRequiredUserRelations([{ requiredType: "Merchant" }])
    const merchantDetails = useSelector(merchantSelectors.merchantDetails)
    const selectedWebshopId =
        props.webshopIds && props.webshopIds?.length > 0 ? props.webshopIds[0] : useSelector(merchantSelectors.selectedWebshopId)

    if (!merchantDetails || !merchantDetails.webshops.length || !selectedWebshopId) {
        return null
    }

    return <TermsTechOnbardingPageInner merchantDetails={merchantDetails} selectedWebshopId={selectedWebshopId} {...props} />
}

const TermsTechOnbardingPageInner = ({
    merchantDetails,
    webshopIds,
    selectedWebshopId,
    isEdit,
    onGoBack,
    onOnboarded,
}: ITermsTechOnboardingPageInnerProps) => {
    const { t } = useTranslation()
    const dispatch = useReduxDispatch()

    const [showCancelService, setShowCancelService] = useState(false)
    const [onboardingDetails, setOnboardingDetails] = useState<ITermsTechGetOnboardingDetailsResponse>()

    useEffect(() => {
        merchantDetails?.webshops.forEach(
            async (w) =>
                await dispatch(
                    getOnboardingStatusThunk(merchantDetails?.swMerchant.swMerchantId, w.webshopId, false, ["TermsTech"])
                )
        )
    }, [merchantDetails])

    useEffect(() => {
        if (!isEdit && selectedWebshopId) {
            ;(async () => {
                const response = await dispatch(
                    getTermsTechOnboardingDetailsThunk(merchantDetails.swMerchant.swMerchantId, selectedWebshopId)
                )
                setOnboardingDetails(response)
            })()
        }
    }, [isEdit, selectedWebshopId])

    const gateway = useSelector<StoreState, IGatewayDto | undefined>((state) =>
        gatewaySelectors.getGateway(
            state,
            merchantDetails.swMerchant.swMerchantId,
            "TermsTech",
            selectedWebshopId || "",
            "Area42"
        )
    )

    const { handleSubmit, register, setValue, errors, formState } = useForm()

    if (!webshopIds && !isEdit) {
        return null
    }

    const onSubmit: SubmitHandler<IFormModel> = async (data: IFormModel) => {
        try {
            const formData: ITermsTechOnboardingConnectData = {
                swMerchantId: merchantDetails.swMerchant.swMerchantId,
                webshopId: selectedWebshopId || "",
                clientId: data.termsTechClientId,
                clientSecret: data.termsTechClientSecret,
                webhookSecret: data.termsTechWebhookSecret,
            }
            await dispatch(termsTechOnboardingConnectThunk(formData))
            onOnboarded()
        } catch {
            //Catch to stop showing spinner
        }
    }

    const onboardingFlowFromStatus = (status: TTermsTechOnboardingStatus): TTermsTechOnboardingFlow => {
        if (status == "Not Started") {
            return "Register"
        }

        if (status == "In Progress" || status == "Submitted") {
            return "Under Review"
        }

        if (status == "Failed") {
            return "Failed"
        }

        if (status == "Successful") {
            return "Onboarded"
        }

        return "Error"
    }

    const onboardingFlow = () => {
        if (onboardingDetails == undefined) {
            return <></>
        }

        if (onboardingDetails.onboardingUrl && onboardingFlowFromStatus(onboardingDetails.onboardingStatus) == "Register") {
            return (
                <>
                    <CleanHtml element="div" className="termstech-text-1" html={t("termstechonboarding.toactivate")} />
                    <StandardButton
                        onClick={(e) => {
                            e.preventDefault()
                            window.open(onboardingDetails.onboardingUrl, "_blank")
                        }}
                        title={t("termstechonboarding.register")}
                        className="swiipe-btn-dark border-0 border-radius-0 mt-2 w-auto px-5"
                    />
                </>
            )
        }

        if (onboardingFlowFromStatus(onboardingDetails.onboardingStatus) == "Onboarded") {
            return <></>
        }

        if (onboardingFlowFromStatus(onboardingDetails.onboardingStatus) == "Failed") {
            return (
                <>
                    <CleanHtml element="div" className="termstech-text-2" html={t("termstechonboarding.denied")} />
                    <CleanHtml
                        element="div"
                        className="termstech-text-2"
                        html={t("termstechonboarding.contactProvider", {
                            email: `<a href="mailto:contact@terms.tech" target="_blank">contact@terms.tech</a>`,
                        })}
                    />
                </>
            )
        }

        if (onboardingFlowFromStatus(onboardingDetails.onboardingStatus) == "Under Review") {
            return <CleanHtml element="div" className="termstech-text-2" html={t("termstechonboarding.underreview")} />
        }

        if (onboardingDetails.errorMessage) {
            console.warn("Terms.Tech onboarding error: " + onboardingDetails.errorMessage)
        }
        return (
            <CleanHtml
                element="div"
                className="termstech-text-2"
                html={t("termstechonboarding.error", {
                    email: `<a href="mailto:support@ecom.aera.id" target="_blank">support@ecom.aera.id</a>`,
                })}
            />
        )
    }

    return (
        <>
            {showCancelService && gateway && (
                <CancelService
                    serviceName={t("termstechonboarding.termstech")}
                    cancelText={t("cancelService.termstech.cancelText", {
                        webshopName: getWebshopHostName(merchantDetails, selectedWebshopId),
                    })}
                    customCancelledText={t("cancelService.termstech.cancelledText")}
                    onCancel={async () => {
                        await dispatch(
                            termsTechDisableOnboardingThunk(
                                merchantDetails.swMerchant.swMerchantId,
                                selectedWebshopId || "",
                                gateway.gatewayId
                            )
                        )

                        await dispatch(
                            sendServiceCancellationEmailThunk(
                                merchantDetails.swMerchant.swMerchantId,
                                "GeneralServiceCancellationEopEmail",
                                t("serviceNames.termsTech"),
                                true
                            )
                        )
                    }}
                    onGoBack={() => setShowCancelService(false)}
                />
            )}
            <PageWrapper className={cn({ "d-none": showCancelService })}>
                {isEdit && <GoBackContainer goBackText={t("commononboardingtexts.backToAllService")} onGoBack={onGoBack} />}
                <PageContainer noHeader={isEdit}>
                    <div className="termstech-onboarding-container">
                        <div className="termstech-form-head">
                            <img className="swiipe-payments" src={getImage("termsTechLogo")} />
                            <div className="swiipe-payments-by-container">
                                <p>by</p>
                                <img className="swiipe-payments--company" src={getImage("area42Logo")} />
                            </div>
                        </div>

                        {!isEdit && (
                            <SpinnerContainer
                                showSpinner={onboardingDetails == null}
                                wrapperClassName="termstech-data-details"
                                minHeight={60}
                            >
                                {onboardingFlow()}
                            </SpinnerContainer>
                        )}

                        {onboardingDetails && onboardingFlowFromStatus(onboardingDetails.onboardingStatus) == "Onboarded" && (
                            <Form onSubmit={handleSubmit(onSubmit)} className="termstech-form">
                                {!isEdit && (
                                    <>
                                        <FixedLabelInput
                                            label={t("termstechonboarding.termstechclientid")}
                                            name="termsTechClientId"
                                            innerRef={register(valFuncRequired(t("termstechonboarding.termsTechClientIdError")))}
                                            placeholder=""
                                        />
                                        <FixedLabelInput
                                            label={t("termstechonboarding.termstechclientsecret")}
                                            name="termsTechClientSecret"
                                            innerRef={register(
                                                valFuncRequired(t("termstechonboarding.termsTechClientSecretError"))
                                            )}
                                            placeholder=""
                                        />
                                        <FixedLabelInput
                                            label={t("termstechonboarding.termstechwebhooksecret")}
                                            name="termsTechWebhookSecret"
                                            innerRef={register(
                                                valFuncRequired(t("termstechonboarding.termsTechWebhookSecretError"))
                                            )}
                                            placeholder=""
                                        />
                                        <div className="termstech-onboarding-form mt-4">
                                            <ShowErrorMessages formState={formState} />

                                            <SubmitButton
                                                dark
                                                noBorder
                                                noBorderRadius
                                                containerClass="submit-btn"
                                                formState={formState}
                                            >
                                                {t("commononboardingtexts.submit")}
                                            </SubmitButton>
                                        </div>
                                    </>
                                )}
                            </Form>
                        )}

                        {!isEdit && (
                            <div className="termstech-back-link mt-4">
                                <a
                                    onClick={() => {
                                        onGoBack()
                                    }}
                                >
                                    {t("commononboardingtexts.back")}
                                </a>
                            </div>
                        )}
                        {isEdit && (
                            <SharedCancelContainer
                                cancelText={t("termstechonboarding.cancel")}
                                onCancel={() => setShowCancelService(true)}
                            />
                        )}
                    </div>
                </PageContainer>
            </PageWrapper>
        </>
    )
}

export default TermsTechOnboardingPage
